import type { TuningConfig } from './config'

/**
 * Per-rocket throttle state: spool lag plus graceful handling of tracking loss
 * (DESIGN.md §2). Holding thrust through a dropout would fling the player;
 * cutting it instantly reads as a bug. Decaying over ~200 ms reads as a rocket.
 */
export class Rocket {
  /** Effective throttle after spool + tracking-loss decay, 0..1. */
  level = 0
  /** Last commanded throttle from the trigger. */
  commanded = 0
  /** 0 = fully lost, 1 = fully tracked. */
  trackingConfidence = 1

  update(commanded: number, tracked: boolean, cfg: TuningConfig, dt: number): number {
    this.commanded = clamp01(commanded)

    if (tracked) {
      this.trackingConfidence = 1
    } else if (cfg.trackingLossDecay > 1e-4) {
      this.trackingConfidence = Math.max(0, this.trackingConfidence - dt / cfg.trackingLossDecay)
    } else {
      this.trackingConfidence = 0
    }

    const target = this.commanded * this.trackingConfidence
    if (cfg.thrustLag > 1e-4) {
      const alpha = 1 - Math.exp(-dt / cfg.thrustLag)
      this.level += (target - this.level) * alpha
    } else {
      this.level = target
    }
    return this.level
  }

  reset(): void {
    this.level = 0
    this.commanded = 0
    this.trackingConfidence = 1
  }
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : Number.isFinite(x) ? x : 0
}
